import * as React from 'react'
import { Download, Loader2 } from 'lucide-react'
import {
  downloadTagBundle,
  tagBundleClientFilename,
  type TagBundleReadings
} from '@axilon-apps/domains-ontology-ui-state'

import { Button } from '../../../components/ui/button'
import { Card, CardContent } from '../../../components/ui/card'
import { cn } from '../../../lib/utils'

function getErrorMessage(error: unknown): string {
  if (error instanceof Error) return error.message
  if (typeof error === 'string') return error
  if (error && typeof error === 'object') {
    const obj = error as Record<string, unknown>
    if (typeof obj['message'] === 'string') return obj['message']
  }
  return 'Unknown error.'
}

const WINDOWS: Array<{ id: string; label: string; hours: number | null }> = [
  { id: 'none', label: 'Registry only', hours: null },
  { id: '1h', label: '+ 1 hour', hours: 1 },
  { id: '24h', label: '+ 24 hours', hours: 24 },
  { id: '7d', label: '+ 7 days', hours: 24 * 7 }
]

/**
 * Exports the organization's tag registry as a portable bundle. Readings are
 * opt-in because tag_readings is by far the largest table, and a week of it can
 * dwarf the registry it accompanies.
 */
export function TagBundleExportCard({ embedded = false }: { embedded?: boolean }) {
  const [busy, setBusy] = React.useState(false)
  const [error, setError] = React.useState('')
  const [savedAs, setSavedAs] = React.useState('')
  const [windowId, setWindowId] = React.useState('none')

  const selected = WINDOWS.find((w) => w.id === windowId) ?? WINDOWS[0]

  const runDownload = React.useCallback(async () => {
    setError('')
    setSavedAs('')
    const fileName = tagBundleClientFilename()
    setBusy(true)
    try {
      const readings: TagBundleReadings = selected.hours === null ? 'none' : 'window'
      const options =
        selected.hours === null
          ? { readings }
          : {
              readings,
              from: new Date(Date.now() - selected.hours * 60 * 60 * 1000).toISOString(),
              to: new Date().toISOString()
            }
      await downloadTagBundle(fileName, options)
      setSavedAs(fileName)
    } catch (err) {
      setError(getErrorMessage(err))
    } finally {
      setBusy(false)
    }
  }, [selected])

  const body = (
    <div className="flex flex-col gap-2">
      <div className="flex items-center justify-between gap-3">
        <div className="min-w-0">
          <div className="text-sm font-medium text-ink-2">Export tag registry</div>
          <p className="m-0 text-11 text-ink-3">
            Portable <span className="font-mono">.ndjson.gz</span>; importable into another
            environment with its gateway URLs remapped
          </p>
        </div>
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="shrink-0"
          onClick={() => void runDownload()}
          disabled={busy}
        >
          {busy ? <Loader2 className="animate-spin" /> : <Download />}
          {busy ? 'Preparing…' : 'Export'}
        </Button>
      </div>

      <div className="flex flex-wrap items-center gap-1">
        {WINDOWS.map(({ id, label }) => (
          <button
            key={id}
            type="button"
            aria-pressed={windowId === id}
            onClick={() => setWindowId(id)}
            disabled={busy}
            className={cn(
              'rounded-md border px-2 py-1 text-11 font-medium transition-colors',
              windowId === id
                ? 'border-border bg-card text-ink shadow-sm'
                : 'border-transparent text-ink-3 hover:bg-card/60 hover:text-ink-2'
            )}
          >
            {label}
          </button>
        ))}
      </div>

      {selected.hours !== null ? (
        <p className="m-0 text-11 text-ink-3">
          Including readings can produce a very large file. Credentials are never exported.
        </p>
      ) : null}

      {error ? (
        <div role="alert" className="rounded-md bg-st-down-bg px-2 py-1 text-11 text-st-down">
          {error}
        </div>
      ) : null}

      {savedAs ? (
        <p className="m-0 text-11 text-ink-2">
          Saved <span className="font-mono">{savedAs}</span>.
        </p>
      ) : null}
    </div>
  )

  if (embedded) {
    return <div className="rounded-xl border bg-card px-4 py-3 shadow-sm">{body}</div>
  }

  return (
    <Card className="gap-0 py-0 shadow-sm">
      <CardContent className="px-4 py-2.5">{body}</CardContent>
    </Card>
  )
}
